import { motion } from "framer-motion";
import { AlertCircle, ArrowRight, Pencil } from "lucide-react";
import type { SlotContribution, SlotKey, SlotState } from "../../slots";

/**
 * ReviewScreen — "here's what we heard". Every derived slot is shown in
 * plain English, and every low-confidence slot is called out with the two
 * conflicting answers side by side. No scoring is shown here — this is
 * the last chance to correct before we match.
 */

export interface ReviewBullet {
  slotKey: SlotKey | null;   // null for narrative-only lines
  text: string;              // "You'd rather be moving than at a desk."
}

export interface ReviewScreenProps {
  slots: SlotState;
  bullets: ReviewBullet[];
  /** Human labels for the slot keys shown on low-confidence conflicts. */
  slotLabels: Partial<Record<SlotKey, string>>;
  onEditSlot?: (key: SlotKey) => void;
  onConfirm: () => void;
}

export function ReviewScreen({
  slots, bullets, slotLabels, onEditSlot, onConfirm,
}: ReviewScreenProps) {
  const lowKeys = Object.keys(slots.lowConfidence) as SlotKey[];

  return (
    <div className="max-w-2xl mx-auto space-y-8">
      <div>
        <div className="text-xs uppercase tracking-[0.2em] text-primary mb-3">
          Before we match you
        </div>
        <h1 className="font-display text-3xl md:text-4xl font-medium leading-tight">
          Here's what we heard.
        </h1>
        <p className="mt-3 text-muted-foreground">
          In your words, not ours. Change anything that doesn't sound like you — nothing is
          matched until you say go.
        </p>
      </div>

      <ul className="space-y-3">
        {bullets.map((b, i) => (
          <motion.li
            key={i}
            initial={{ opacity: 0, y: 6 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: i * 0.03, duration: 0.24 }}
            className="rounded-2xl border border-border bg-card p-4 flex items-start gap-3"
          >
            <span className="mt-1 h-1.5 w-1.5 rounded-full bg-primary" />
            <span className="flex-1 text-base text-foreground/90">{b.text}</span>
          </motion.li>
        ))}
      </ul>

      {lowKeys.length > 0 && (
        <div className="rounded-3xl border border-amber-500/40 bg-amber-500/5 p-5 space-y-4">
          <div className="flex items-center gap-2 text-amber-500">
            <AlertCircle className="h-4 w-4" />
            <div className="text-xs uppercase tracking-[0.2em] font-medium">
              A couple of answers pulled in different directions
            </div>
          </div>
          <p className="text-sm text-muted-foreground">
            That's normal — people are rarely one thing. We'll take the gentler of the two so
            nothing gets suggested that would wear you down. Change it here if we read it wrong.
          </p>
          <div className="space-y-3">
            {lowKeys.map((k) => (
              <ConflictCard
                key={k}
                slotKey={k}
                label={slotLabels[k] ?? k}
                contribs={slots.contribs[k] ?? []}
                onEdit={onEditSlot ? () => onEditSlot(k) : undefined}
              />
            ))}
          </div>
        </div>
      )}

      <div className="flex justify-end pt-2">
        <button
          onClick={onConfirm}
          className="min-h-[48px] px-6 rounded-2xl bg-primary text-primary-foreground font-medium
            inline-flex items-center gap-2
            focus:outline-none focus-visible:ring-2 focus-visible:ring-primary"
        >
          That's me — show me what fits <ArrowRight className="h-4 w-4" />
        </button>
      </div>
    </div>
  );
}

function ConflictCard({
  slotKey, label, contribs, onEdit,
}: {
  slotKey: SlotKey; label: string; contribs: SlotContribution[]; onEdit?: () => void;
}) {
  return (
    <div className="rounded-2xl border border-border bg-background p-4">
      <div className="flex items-start justify-between gap-3">
        <div className="text-sm font-medium">{label}</div>
        {onEdit && (
          <button
            onClick={onEdit}
            aria-label={`Fix ${slotKey}`}
            className="text-xs text-primary hover:underline inline-flex items-center gap-1"
          >
            <Pencil className="h-3 w-3" /> Fix
          </button>
        )}
      </div>
      <div className="mt-3 space-y-1.5">
        {contribs.map((c, i) => (
          <div key={i} className="text-xs text-muted-foreground flex justify-between">
            <span>{c.label ?? c.screenId}</span>
            <span className="tabular-nums">{Math.round(c.value)}</span>
          </div>
        ))}
      </div>
    </div>
  );
}
